import React, { useState, useCallback } from 'react';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { insertProfileDocument, updateProfileDocument } from '@/services/jobintelProfileService';
import { FileTextExtractor } from '@/utils/fileTextExtractor';
import type { JobIntelUserProfile } from '@/types/jobintelProfile';

interface Props {
  onComplete: (data: Partial<JobIntelUserProfile>, fieldMeta: Record<string, { confidence: string; source?: string }>) => void;
  onBack: () => void;
}

interface UploadedFile {
  file: File;
  docId?: string;
  status: 'uploading' | 'extracting' | 'parsing' | 'done' | 'error';
  text?: string;
  error?: string;
}

export function DocumentUploadPath({ onComplete, onBack }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [parsing, setParsing] = useState(false);

  const handleFiles = useCallback(async (fileList: FileList) => {
    const newFiles = Array.from(fileList).filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'docx', 'doc', 'txt'].includes(ext || '');
    });

    if (newFiles.length === 0) {
      toast({ title: 'Invalid file type', description: 'Please upload PDF, DOCX, or TXT files.', variant: 'destructive' });
      return;
    }

    for (const file of newFiles) {
      const entry: UploadedFile = { file, status: 'uploading' };
      setFiles(prev => [...prev, entry]);

      try {
        // Record in profile_documents table
        const docType = file.name.toLowerCase().includes('linkedin') ? 'linkedin_export'
          : file.name.toLowerCase().includes('worksheet') ? 'worksheet'
          : 'resume';

        const docRecord = await insertProfileDocument({
          user_id: user!.id,
          document_type: docType,
          original_filename: file.name,
          file_size_bytes: file.size,
          parse_status: 'parsing',
        });

        setFiles(prev => prev.map(f => f.file === file ? { ...f, status: 'extracting', docId: docRecord.id } : f));

        // Extract text
        const result = await FileTextExtractor.extractText(file);
        const text = result.success ? (result.text || '') : '';

        setFiles(prev => prev.map(f => f.file === file ? { ...f, status: 'done', text } : f));

        await updateProfileDocument(docRecord.id!, { parse_status: 'complete', parsed_at: new Date().toISOString() });
      } catch (err: any) {
        setFiles(prev => prev.map(f => f.file === file ? { ...f, status: 'error', error: err.message } : f));
      }
    }
  }, [user, toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleBuildProfile = async () => {
    const allText = files.filter(f => f.status === 'done' && f.text).map(f => `--- ${f.file.name} ---\n${f.text}`).join('\n\n');

    if (!allText.trim()) {
      toast({ title: 'No text extracted', description: 'Could not extract text from any uploaded files.', variant: 'destructive' });
      return;
    }

    setParsing(true);

    try {
      // Call edge function to parse documents into profile fields
      const { data, error } = await supabase.functions.invoke('ai-resume-parser', {
        body: { resumeText: allText, parseMode: 'jobintel_profile' }
      });

      if (error) throw error;

      // Build profile data and field meta from the response
      const profileData: Partial<JobIntelUserProfile> = {};
      const fieldMeta: Record<string, { confidence: string; source?: string }> = {};

      if (data?.parsed) {
        const parsed = data.parsed;
        // Map parsed fields to profile
        if (parsed.current_job_title) { profileData.current_job_title = parsed.current_job_title; fieldMeta.current_job_title = { confidence: 'extracted' }; }
        if (parsed.current_industry) { profileData.current_industry = parsed.current_industry; fieldMeta.current_industry = { confidence: 'inferred' }; }
        if (parsed.years_of_experience) { profileData.years_of_experience = parsed.years_of_experience; fieldMeta.years_of_experience = { confidence: 'inferred' }; }
        if (parsed.skills?.length) { profileData.core_skills = parsed.skills; fieldMeta.core_skills = { confidence: 'extracted' }; }
        if (parsed.education_level) { profileData.education_level = parsed.education_level; fieldMeta.education_level = { confidence: 'extracted' }; }
        if (parsed.education_field) { profileData.education_field = parsed.education_field; fieldMeta.education_field = { confidence: 'extracted' }; }
        if (parsed.certifications?.length) { profileData.certifications = parsed.certifications; fieldMeta.certifications = { confidence: 'extracted' }; }
        if (parsed.location) {
          const parts = parsed.location.split(',').map((s: string) => s.trim());
          if (parts[0]) { profileData.current_city = parts[0]; fieldMeta.current_city = { confidence: 'extracted' }; }
          if (parts[1]) { profileData.current_state = parts[1]; fieldMeta.current_state = { confidence: 'extracted' }; }
        }
        if (parsed.experience_level) { profileData.experience_level = parsed.experience_level; fieldMeta.experience_level = { confidence: 'inferred' }; }
        if (parsed.summary) { profileData.career_summary = parsed.summary; fieldMeta.career_summary = { confidence: 'extracted' }; }
        if (parsed.notable_employers?.length) { profileData.notable_employers = parsed.notable_employers; fieldMeta.notable_employers = { confidence: 'extracted' }; }
      }

      profileData.resume_text = allText;
      profileData.setup_method = 'documents_uploaded';

      onComplete(profileData, fieldMeta);
    } catch (err: any) {
      toast({ title: 'Parsing failed', description: err.message, variant: 'destructive' });
    } finally {
      setParsing(false);
    }
  };

  const readyCount = files.filter(f => f.status === 'done').length;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Upload Your Documents</h2>
        <p className="text-muted-foreground">Drop your resume, worksheets, or LinkedIn export below. We'll read them and build your profile automatically.</p>
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        className="border-2 border-dashed border-primary/30 rounded-xl p-10 text-center hover:border-primary/60 hover:bg-primary/5 transition-all cursor-pointer"
        onClick={() => document.getElementById('profile-file-input')?.click()}
      >
        <Upload className="h-10 w-10 text-primary mx-auto mb-3" />
        <p className="text-foreground font-medium">Drag & drop files here or click to browse</p>
        <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, or TXT — up to 20MB each</p>
        <input
          id="profile-file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt"
          className="hidden"
          onChange={e => e.target.files && handleFiles(e.target.files)}
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <Card key={i} className="border">
              <CardContent className="p-3 flex items-center gap-3">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 text-sm font-medium truncate">{f.file.name}</span>
                {f.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                {f.status === 'extracting' && <span className="text-xs text-primary">Reading...</span>}
                {f.status === 'done' && <CheckCircle className="h-4 w-4 text-[hsl(var(--success))]" />}
                {f.status === 'error' && <AlertCircle className="h-4 w-4 text-destructive" />}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button
          onClick={handleBuildProfile}
          disabled={readyCount === 0 || parsing}
          className="bg-[hsl(var(--primary))]"
        >
          {parsing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Reading your documents and building your profile...
            </>
          ) : (
            `Build Profile from ${readyCount} Document${readyCount !== 1 ? 's' : ''}`
          )}
        </Button>
      </div>
    </div>
  );
}
